import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { ErrorWrapper } from "service/api/estabrookFetcher";
import { reportError } from "../error-reporting";
import { triggerBlobDownload } from "../utils/download-file";
import { PlowReportFile } from "../service/plow-report";

const isErrorWrapper = (error: unknown): error is ErrorWrapper<undefined> =>
  (error as ErrorWrapper<undefined>)?.errors !== undefined;

const getErrorMessage = (error: unknown): string => {
  if (isErrorWrapper(error) && error?.errors?.length) {
    return error.errors[0].detail;
  }
  return "Failed to download plow report. Please try again.";
};

export interface DownloadOptions {
  /** Show a success toast on completion. Defaults to true. */
  withSuccessToast?: boolean;
  /** Show an error toast on failure. Defaults to true. Modal flows disable this
   *  and render the error inline instead. */
  withErrorToast?: boolean;
  /** Custom failure handler. When provided it takes over feedback (the default
   *  error toast is suppressed), so callers can tailor the message — e.g. treat
   *  an empty date range as a gentle info notice rather than an error. */
  onError?: (message: string, error: unknown) => void;
}

/**
 * Wraps the plow-report download UX: runs the fetch, saves the file under the
 * name the backend sent, and surfaces success/error feedback. Exposes
 * `isLoading` and `error` so a dialog can render loading and inline-error
 * states, and a `reset` to clear the error (e.g. on retry or close).
 */
export const usePlowReportDownload = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = useCallback(
    async (
      fetchFn: () => Promise<PlowReportFile>,
      opts: DownloadOptions = {},
    ): Promise<boolean> => {
      const { withSuccessToast = true, withErrorToast = true, onError } = opts;

      setIsLoading(true);
      setError(null);

      try {
        const { blob, filename } = await fetchFn();
        triggerBlobDownload(blob, filename);
        if (withSuccessToast) {
          toast.success("Plow report downloaded");
        }
        return true;
      } catch (e) {
        reportError(e, { tags: { feature: "plow-report-download" } });

        const message = getErrorMessage(e);
        setError(message);
        if (onError) {
          onError(message, e);
        } else if (withErrorToast) {
          toast.error(message);
        }
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => setError(null), []);

  return { download, isLoading, error, reset };
};
